
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Activity, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  TrendingUp,
  Users,
  BookOpen
} from 'lucide-react';
import { useUserManagement } from '@/hooks/useUserManagement';

interface AssignmentProgress {
  id: string;
  userId: string;
  userName: string;
  courseId: string;
  courseTitle: string;
  progress: number;
  status: 'active' | 'completed' | 'overdue';
  lastActivity: string;
  dueDate?: string;
  timeSpent: number;
}

const RealTimeAssignmentTracker: React.FC = () => {
  const { users, loading } = useUserManagement();
  const [assignments, setAssignments] = useState<AssignmentProgress[]>([]);
  const [refreshInterval, setRefreshInterval] = useState(5000); // 5 seconds

  // Simulate real-time assignment data
  useEffect(() => {
    const generateMockAssignments = (): AssignmentProgress[] => {
      return users.slice(0, 10).map((user, index) => ({
        id: `assignment-${user.id}-${index}`,
        userId: user.id,
        userName: user.name,
        courseId: `course-${index % 3 + 1}`,
        courseTitle: ['React Fundamentals', 'SQL Database Design', 'AWS Solutions Architect'][index % 3],
        progress: Math.floor(Math.random() * 100),
        status: ['active', 'completed', 'overdue'][Math.floor(Math.random() * 3)] as any,
        lastActivity: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
        dueDate: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
        timeSpent: Math.floor(Math.random() * 120) // minutes
      }));
    };

    if (users.length > 0) {
      setAssignments(generateMockAssignments());
    }
  }, [users]);

  // Simulate real-time updates
  useEffect(() => {
    if (assignments.length === 0) return;

    const interval = setInterval(() => {
      setAssignments(prev => prev.map(assignment => ({
        ...assignment,
        progress: Math.min(100, assignment.progress + Math.floor(Math.random() * 3)),
        lastActivity: Math.random() > 0.7 ? new Date().toISOString() : assignment.lastActivity,
        timeSpent: assignment.timeSpent + Math.floor(Math.random() * 5)
      })));
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [assignments.length, refreshInterval]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'active':
        return <Clock className="h-4 w-4 text-blue-600" />;
      case 'overdue':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'default';
      case 'active':
        return 'secondary';
      case 'overdue':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const activeAssignments = assignments.filter(a => a.status === 'active');
  const completedAssignments = assignments.filter(a => a.status === 'completed');
  const overdueAssignments = assignments.filter(a => a.status === 'overdue');

  const averageProgress = assignments.length > 0 
    ? Math.round(assignments.reduce((sum, a) => sum + a.progress, 0) / assignments.length)
    : 0;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading assignment tracker...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Real-Time Assignment Tracker</h2>
          <p className="text-muted-foreground">Monitor assignment progress and user activity in real-time</p>
        </div>
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-green-600 animate-pulse" />
          <span className="text-sm text-green-600">Live Updates</span>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Assignments</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{assignments.length}</div>
            <p className="text-xs text-muted-foreground">
              Active tracking
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeAssignments.length}</div>
            <p className="text-xs text-muted-foreground">
              In progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedAssignments.length}</div>
            <p className="text-xs text-muted-foreground">
              Finished
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Progress</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{averageProgress}%</div>
            <p className="text-xs text-muted-foreground">
              Overall completion
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">All Assignments</TabsTrigger>
          <TabsTrigger value="active">
            Active 
            {activeAssignments.length > 0 && (
              <Badge variant="secondary" className="ml-2">{activeAssignments.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="overdue">
            Overdue
            {overdueAssignments.length > 0 && (
              <Badge variant="destructive" className="ml-2">{overdueAssignments.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <div className="grid gap-4">
            {assignments.map((assignment) => (
              <Card key={assignment.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-blue-600" />
                      <div>
                        <h3 className="font-semibold">{assignment.userName}</h3>
                        <p className="text-sm text-muted-foreground">{assignment.courseTitle}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(assignment.status)}
                      <Badge variant={getStatusColor(assignment.status) as any}>
                        {assignment.status}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Progress</span>
                      <span className="font-medium">{assignment.progress}%</span>
                    </div>
                    <Progress value={assignment.progress} className="h-2" />
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-3">
                    <span>Last activity: {new Date(assignment.lastActivity).toLocaleString()}</span>
                    <span>Time spent: {assignment.timeSpent} min</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="active" className="space-y-4">
          <div className="grid gap-4">
            {activeAssignments.map((assignment) => (
              <Card key={assignment.id} className="border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-blue-600" />
                      <div>
                        <h3 className="font-semibold">{assignment.userName}</h3>
                        <p className="text-sm text-muted-foreground">{assignment.courseTitle}</p>
                      </div>
                    </div>
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Progress</span>
                      <span className="font-medium">{assignment.progress}%</span>
                    </div>
                    <Progress value={assignment.progress} className="h-2" />
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-3">
                    <span>Last activity: {new Date(assignment.lastActivity).toLocaleString()}</span>
                    <span>Time spent: {assignment.timeSpent} min</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="overdue" className="space-y-4">
          <div className="grid gap-4">
            {overdueAssignments.map((assignment) => (
              <Card key={assignment.id} className="border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-red-600" />
                      <div>
                        <h3 className="font-semibold">{assignment.userName}</h3>
                        <p className="text-sm text-muted-foreground">{assignment.courseTitle}</p>
                      </div>
                    </div>
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Progress</span>
                      <span className="font-medium text-red-600">{assignment.progress}%</span>
                    </div>
                    <Progress value={assignment.progress} className="h-2" />
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-3">
                    <span>Due: {assignment.dueDate ? new Date(assignment.dueDate).toLocaleDateString() : 'No due date'}</span>
                    <span className="text-red-600">OVERDUE</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="completed" className="space-y-4">
          <div className="grid gap-4">
            {completedAssignments.map((assignment) => (
              <Card key={assignment.id} className="border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-green-600" />
                      <div>
                        <h3 className="font-semibold">{assignment.userName}</h3>
                        <p className="text-sm text-muted-foreground">{assignment.courseTitle}</p>
                      </div>
                    </div>
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Completed</span>
                      <span className="font-medium text-green-600">100%</span>
                    </div>
                    <Progress value={100} className="h-2" />
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-3">
                    <span>Completed: {new Date(assignment.lastActivity).toLocaleDateString()}</span>
                    <span>Total time: {assignment.timeSpent} min</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RealTimeAssignmentTracker;
